import { useEffect, useState } from "react";
import api from "@/lib/api";
import { CaretDown, CheckCircle, Circle, Clock } from "@phosphor-icons/react";

const PILL = {
  not_started: { label: "Not started", cls: "bg-slate-100 text-slate-600 border-slate-200", Icon: Circle, style: undefined },
  in_progress: { label: "In progress", cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: Clock, style: undefined },
  signed_off: { label: "Signed off", cls: "bg-positive/10 border-positive/30", Icon: CheckCircle, style: { color: "#00885b" } },
};

export function MonthEndStatusPill({ status }) {
  if (!status) return null;
  const meta = PILL[status.state] || PILL.not_started;
  const Icon = meta.Icon;
  return (
    <span data-testid="month-end-status-pill" data-state={status.state}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${meta.cls}`}
      style={meta.style}>
      <Icon size={13} weight="fill" /> {status.label || meta.label}
    </span>
  );
}

export default function MonthEndBar({ onPeriodChange }) {
  const [periods, setPeriods] = useState([]);
  const [sel, setSel] = useState("");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (periodId) => {
    setLoading(true);
    try {
      const url = periodId ? `/month-end/status?period_id=${encodeURIComponent(periodId)}` : "/month-end/status";
      const { data } = await api.get(url);
      setStatus(data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/close-periods").then(({ data }) => setPeriods(data.periods || [])).catch(() => {});
    load("");
  }, []);

  const pick = (v) => {
    setSel(v);
    load(v);
    onPeriodChange && onPeriodChange(v, periods.find((p) => p.id === v) || null);
  };

  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="month-end-bar">
      <div className="flex items-center gap-2">
        <span className="overline text-navy/60">Reporting period</span>
        <div className="relative">
          <select data-testid="period-selector" value={sel} onChange={(e) => pick(e.target.value)}
            className="appearance-none bg-white border border-slate-200 rounded-lg pl-3 pr-8 py-1.5 text-sm font-medium text-navy hover:border-slate-400 focus:outline-none focus:border-orange cursor-pointer">
            <option value="">Current period</option>
            {periods.map((p) => (
              <option key={p.id} value={p.id}>{p.title}{p.status === "Closed" ? " (signed off)" : ""}</option>
            ))}
          </select>
          <CaretDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-navy/40 pointer-events-none" />
        </div>
      </div>
      {loading
        ? <span className="text-xs text-navy/40 font-mono tracking-wider" data-testid="month-end-loading">…</span>
        : <MonthEndStatusPill status={status} />}
      {status?.state === "not_started" && status?.last_signoff?.signed_off_at && (
        <span className="text-xs text-navy/50" data-testid="last-signoff-note">
          Last signed off: {status.last_signoff.title}{status.last_signoff.signed_off_by ? ` · ${status.last_signoff.signed_off_by}` : ""}
        </span>
      )}
    </div>
  );
}
